import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Copy, Download, Loader2, Save, Sparkles, Trash2, Wand2, ChevronDown, RefreshCcw, Mail, GripVertical,
} from "lucide-react";
import { exportQuotePdf, quoteToPlainText } from "@/lib/quotePdf";
import { useConfirm } from "@/context/ConfirmContext";
import EmailQuoteDialog from "@/components/EmailQuoteDialog";

const STATUSES = ["Draft", "Sent", "Approved", "Rejected", "Paid"];

export default function QuoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refining, setRefining] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const confirm = useConfirm();

  useEffect(() => {
    api.get(`/quotes/${id}`)
      .then((r) => {
        // Attach stable client-side IDs to items so React keys remain stable across edits/reorders.
        const data = r.data;
        if (data?.generated?.items) {
          data.generated.items = data.generated.items.map((it) => ({
            _key: it._key || crypto.randomUUID(),
            ...it,
          }));
        }
        setQuote(data);
      })
      .catch(() => toast.error("Quote not found"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    const logoPath = quote?.company?.logo_url;
    if (!logoPath) { setLogoDataUrl(null); return; }
    // Sign the URL, fetch as blob, convert to data URL (jsPDF needs data URL to embed).
    api.post("/files/sign", { path: logoPath })
      .then((r) => fetch(`${process.env.REACT_APP_BACKEND_URL}${r.data.url}`))
      .then((res) => res.blob())
      .then((blob) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      }))
      .then((dataUrl) => setLogoDataUrl(dataUrl))
      .catch(() => setLogoDataUrl(null));
  }, [quote?.company?.logo_url]);

  if (loading) return <div className="text-slate-400 py-12 text-center">Loading…</div>;
  if (!quote) return <div className="text-slate-500">Not found.</div>;

  const g = quote.generated || {};
  const updateGen = (patch) => setQuote((q) => ({ ...q, generated: { ...q.generated, ...patch } }));
  const updateItem = (i, patch) => {
    const items = [...(g.items || [])];
    items[i] = { ...items[i], ...patch };
    updateGen({ items });
  };
  const removeItem = (i) => updateGen({ items: g.items.filter((_, idx) => idx !== i) });
  const addItem = () => updateGen({ items: [...(g.items || []), { _key: crypto.randomUUID(), description: "", price: 0 }] });

  const reorderItems = (fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx == null || toIdx == null) return;
    const items = [...(g.items || [])];
    const [moved] = items.splice(fromIdx, 1);
    items.splice(toIdx, 0, moved);
    updateGen({ items });
  };

  const recomputeTotal = () => (g.items || []).reduce((s, it) => s + Number(it.price || 0), 0);

  const onSave = async () => {
    setSaving(true);
    try {
      const total = g.items?.length ? recomputeTotal() : Number(quote.total_price || 0);
      const r = await api.put(`/quotes/${id}`, {
        generated: quote.generated,
        total_price: total,
        status: quote.status,
        customer: quote.customer,
        company: quote.company,
      });
      setQuote(r.data);
      toast.success("Quote saved.");
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onRefine = async (instruction) => {
    setRefining(true);
    try {
      const r = await api.post("/quotes/refine", {
        generated: quote.generated,
        instruction,
        rough_notes: quote.rough_notes,
        tone: "Professional",
      });
      const refined = r.data;
      if (refined?.items) {
        refined.items = refined.items.map((it) => ({ _key: crypto.randomUUID(), ...it }));
      }
      updateGen(refined);
      toast.success("Updated.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refine failed");
    } finally {
      setRefining(false);
    }
  };

  const onCopy = async () => {
    const text = quoteToPlainText(quote);
    await navigator.clipboard.writeText(text);
    toast.success("Quote copied to clipboard.");
  };

  const onPdf = () => {
    exportQuotePdf(quote, logoDataUrl);
    toast.success("PDF downloaded.");
  };

  const onStatusChange = async (status) => {
    setQuote((q) => ({ ...q, status }));
    try {
      await api.put(`/quotes/${id}`, { status });
    } catch (e) {
      console.warn("Status update failed:", e);
      toast.error("Couldn't save status");
    }
  };

  const onDelete = async () => {
    const ok = await confirm({
      title: "Delete this quote?",
      description: "This permanently removes the quote and cannot be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/quotes/${id}`);
    toast.success("Deleted.");
    navigate("/app/quotes");
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="quote-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 no-print">
        <div className="flex items-center gap-3">
          <Link to="/app/quotes" className="text-slate-500 hover:text-slate-900" data-testid="back-to-quotes">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="font-display text-2xl font-bold text-slate-900 truncate">{g.project_title || "Quote"}</h1>
          <Badge className="bg-slate-100 text-slate-700">{quote.quote_type}</Badge>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={quote.status} onValueChange={onStatusChange}>
            <SelectTrigger className="w-32" data-testid="status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={refining} data-testid="refine-dropdown-trigger">
                {refining ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
                Refine <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onRefine("shorter")} data-testid="refine-shorter">Make it shorter</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onRefine("more_detailed")} data-testid="refine-detailed">Make it more detailed</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onRefine("more_premium")} data-testid="refine-premium">Make it sound more premium</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onRefine("less_fancy")} data-testid="refine-less-fancy">Make it less fancy</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onRefine("regenerate_scope")} data-testid="refine-scope">
                <RefreshCcw className="h-4 w-4 mr-2" /> Regenerate scope of work
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onRefine("regenerate_notes")} data-testid="refine-notes">
                <RefreshCcw className="h-4 w-4 mr-2" /> Regenerate notes
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button variant="outline" onClick={onCopy} data-testid="copy-button"><Copy className="h-4 w-4 mr-2" />Copy</Button>
          <Button variant="outline" onClick={onPdf} data-testid="pdf-button"><Download className="h-4 w-4 mr-2" />PDF</Button>
          <Button variant="outline" onClick={() => setEmailOpen(true)} data-testid="email-button">
            <Mail className="h-4 w-4 mr-2" />Email
          </Button>
          <Button onClick={onSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="save-button">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save
          </Button>
          <Button variant="ghost" size="icon" onClick={onDelete} data-testid="delete-button"><Trash2 className="h-4 w-4 text-rose-500" /></Button>
        </div>
      </div>

      {/* Document preview */}
      <Card className="p-8 sm:p-10 border-slate-200 shadow-card bg-white" data-testid="quote-preview">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 pb-6 border-b border-slate-200">
          <div className="flex items-start gap-4">
            {logoDataUrl && (
              <img src={logoDataUrl} alt="Logo" className="h-14 w-14 object-contain rounded-md" data-testid="quote-logo" />
            )}
            <div>
              <div className="font-display text-2xl font-bold text-slate-900">{quote.company?.company_name || "Your Company"}</div>
              <div className="text-sm text-slate-500 mt-1 space-x-2">
                {quote.company?.phone && <span>{quote.company.phone}</span>}
                {quote.company?.email && <span>· {quote.company.email}</span>}
                {quote.company?.address && <span>· {quote.company.address}</span>}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wider text-slate-400">{quote.quote_type}</div>
            <div className="text-sm text-slate-600 mt-1">{new Date(quote.created_at).toLocaleDateString()}</div>
          </div>
        </div>

        {/* Customer block */}
        <div className="grid sm:grid-cols-2 gap-6 py-6 border-b border-slate-200">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Quote for</div>
            <div className="font-medium text-slate-900">{quote.customer?.name || "—"}</div>
            <div className="text-sm text-slate-600">{quote.customer?.phone}</div>
            <div className="text-sm text-slate-600">{quote.customer?.email}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Job address</div>
            <div className="text-sm text-slate-700">{quote.customer?.address || "—"}</div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-1 mt-3">Prepared by</div>
            <div className="text-sm text-slate-700">{quote.company?.contractor_name || "—"}</div>
          </div>
        </div>

        {/* Project title */}
        <div className="py-6 border-b border-slate-200">
          <Input
            value={g.project_title || ""}
            onChange={(e) => updateGen({ project_title: e.target.value })}
            className="font-display text-xl font-bold border-none px-0 focus-visible:ring-0"
            data-testid="edit-project-title"
          />
        </div>

        {/* Scope of work */}
        <div className="py-6 border-b border-slate-200">
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Scope of work</div>
          <Textarea
            value={g.scope_of_work || ""}
            onChange={(e) => updateGen({ scope_of_work: e.target.value })}
            className="min-h-[140px] text-slate-800 leading-relaxed border-slate-200"
            data-testid="edit-scope"
          />
        </div>

        {/* Items */}
        {(g.items?.length > 0 || true) && (
          <div className="py-6 border-b border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-wider text-slate-400">Itemized breakdown</div>
              <Button variant="ghost" size="sm" onClick={addItem} data-testid="add-item-button">+ Add item</Button>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-500 border-b border-slate-200">
                  <th className="w-6"></th>
                  <th className="text-left font-medium py-2 w-10">#</th>
                  <th className="text-left font-medium py-2">Description</th>
                  <th className="text-right font-medium py-2 w-28">Price</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {(g.items || []).map((it, i) => (
                  <tr
                    key={it._key || `item-${i}`}
                    data-testid={`item-row-${i}`}
                    draggable
                    onDragStart={(e) => {
                      setDragFrom(i);
                      e.dataTransfer.effectAllowed = "move";
                      // Firefox needs data to start drag
                      e.dataTransfer.setData("text/plain", String(i));
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      if (dragOver !== i) setDragOver(i);
                    }}
                    onDragLeave={() => setDragOver((cur) => (cur === i ? null : cur))}
                    onDrop={(e) => {
                      e.preventDefault();
                      reorderItems(dragFrom, i);
                      setDragFrom(null);
                      setDragOver(null);
                    }}
                    onDragEnd={() => { setDragFrom(null); setDragOver(null); }}
                    className={`border-b border-slate-100 ${dragOver === i && dragFrom !== i ? "bg-orange-50" : ""} ${dragFrom === i ? "opacity-50" : ""}`}
                  >
                    <td className="py-2 cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500" aria-label="Drag to reorder">
                      <GripVertical className="h-4 w-4" />
                    </td>
                    <td className="py-2 text-slate-500">{i + 1}</td>
                    <td className="py-2">
                      <Input
                        value={it.description || ""} onChange={(e) => updateItem(i, { description: e.target.value })}
                        className="border-none focus-visible:ring-0 px-0"
                        data-testid={`item-desc-${i}`}
                      />
                    </td>
                    <td className="py-2 text-right">
                      <Input
                        type="number" step="0.01" value={it.price || 0}
                        onChange={(e) => updateItem(i, { price: Number(e.target.value) })}
                        className="border-none focus-visible:ring-0 text-right px-0"
                        data-testid={`item-price-${i}`}
                      />
                    </td>
                    <td>
                      <Button variant="ghost" size="icon" onClick={() => removeItem(i)} data-testid={`item-remove-${i}`}>
                        <Trash2 className="h-3.5 w-3.5 text-slate-400" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Total */}
        <div className="py-6 border-b border-slate-200 flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-slate-400">Total project cost</div>
          <div className="font-display text-3xl font-bold text-slate-900" data-testid="total-display">
            ${Number(g.items?.length ? recomputeTotal() : quote.total_price || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>

        {/* Notes */}
        <div className="py-6 border-b border-slate-200">
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Notes</div>
          <Textarea
            value={(g.notes || []).join("\n")}
            onChange={(e) => updateGen({ notes: e.target.value.split("\n").filter((x) => x.trim()) })}
            className="min-h-[100px] text-sm text-slate-700 border-slate-200"
            data-testid="edit-notes"
          />
        </div>

        {/* Customer message */}
        <div className="py-6">
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Customer message</div>
          <Textarea
            value={g.customer_message || ""}
            onChange={(e) => updateGen({ customer_message: e.target.value })}
            className="min-h-[80px] text-sm text-slate-700 italic border-slate-200"
            data-testid="edit-customer-message"
          />
        </div>

        {/* Signature */}
        <div className="grid sm:grid-cols-2 gap-6 pt-6 border-t border-slate-200 text-sm text-slate-500">
          <div>Customer Approval: ____________________</div>
          <div>Date: ____________________</div>
        </div>
      </Card>

      <EmailQuoteDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        quote={quote}
        logoDataUrl={logoDataUrl}
        onSent={() => setQuote((q) => ({ ...q, status: q.status === "Draft" ? "Sent" : q.status }))}
      />
    </div>
  );
}
